/**
 * Per-toolset contract numbers (epic PI-SPEC-SW-E279).
 *
 * SpecTree and SpecTree Toolbox release separately. Each toolset carries one
 * integer: bump it only for a breaking change to that toolset's bridge,
 * events or API schemas; additive changes keep the number. The package
 * version itself follows the SpecTree release and says nothing about
 * compatibility.
 *
 * - The app reports the numbers it was built with on
 *   `window.spectreeToolbox.app.contracts`.
 * - SpecTree reports the range it serves on `GET /api/v1/version` as
 *   `toolboxContracts: { <toolset>: { current, min } }`.
 * - {@link compareContract} turns the two into a support state.
 */
import { z } from "zod";
/**
 * The contract number of every toolset this package describes.
 *
 * grooming 2 (epic PI-SPEC-SW-E280): adds the `item-progress` event,
 * `app.titleBar`, `app.notify`, drafted `questions` on `draftAuthorQuestions`
 * and the author-question draft API. SpecTree still serves contract 1
 * (`min: 1`): a contract-1 app sends nothing new.
 *
 * formulator 1 (epic PI-SPEC-SW-E282): the Formulator interview toolset —
 * `spectreeToolbox.formulator`, its events, the engine turn SpecTree accepts
 * on `/reply`, the `turn.json` input and the `SPECTREE_FORMULATOR_TURN`
 * marker line. Adding it leaves grooming at 2.
 *
 * formulator 2 (epic PI-SPEC-SW-E283): adds the optional `persona` on
 * `turn.json` and on the interview view, so the Formulator asks a QA Tester
 * tester questions. SpecTree still serves formulator 1 (`min: 1`): a
 * contract-1 app sends no persona and gets the `product-owner` interview.
 *
 * formulator 3 (epic PI-SPEC-SW-E321, ER-1706): Claude drafts the final item
 * text. Adds the optional `draft` on the engine turn and the
 * `SPECTREE_FORMULATOR_TURN` marker (`formulatorDraftSchema`), the optional
 * `purpose`, `draftBasis`, `currentDraft` and `redraftInstruction` on
 * `turn.json` (schema version stays 1), the optional `paths` on a finding
 * and the optional `formulator.redraft` bridge method. Every addition is
 * optional, so SpecTree keeps serving formulator 2 (and 1): a contract-2 app
 * sends no draft and SpecTree renders the item from the answers as before;
 * pages feature-detect `redraft`. Leaves every other toolset unchanged.
 *
 * formulator 4 (epic PI-SPEC-SW-E322, ER-1709): inferred values are
 * confirmed, not silently kept. Adds the optional `inferred` part keys on the
 * engine turn and the `SPECTREE_FORMULATOR_TURN` marker (each a key of the
 * same turn's `extracted`, at most once), the optional `confirmed` on
 * slot-guide parts, the optional `confirmInferred: true` on `turn.json`
 * (interview turns only; schema version stays 1) and on the interview view,
 * and the optional-offer skip quick pick `FORMULATOR_OPTIONAL_SKIP_PICK`.
 * The marker and turn body are strict, so `inferred` is sent only when
 * `turn.json` carries `confirmInferred`, which the engine copies only from a
 * view that carries it; an older Toolbox strips `confirmed` from the
 * non-strict part objects. Every addition is optional, so SpecTree keeps
 * serving formulator 3, 2 and 1 (`min: 1`). Leaves every other toolset
 * unchanged.
 *
 * grooming 3 (epic PI-SPEC-SW-E284): the run queue. Adds the optional queue
 * methods on `spectreeToolbox.grooming` (`getQueue`, `enqueueItem`,
 * `enqueueBatch`, `enqueueProductAreas`, `enqueueAuthorQuestions`,
 * `removeFromQueue`, `moveInQueue`, `stopQueueEntry`, `stopQueue`,
 * `retryQueueEntry`, `resumeQueue`, `clearCompletedQueue`,
 * `setQueueConcurrency`), the `queue-changed` event, the queue shapes in
 * `grooming/queue.ts`, the batch heartbeat route, the `queued`/`running`
 * state of the backlog `preparing` marker, the `ADO_PAT_INVALID` engine
 * code and the engine item status `current` (a follower item another run on
 * the same computer already briefed; SpecTree checks the brief is still
 * current). In a contract-3 app `groomOne`, `runBatch`, `draftProductAreas` and
 * `draftAuthorQuestions` enqueue. SpecTree still serves grooming 1 and 2
 * (`min: 1`): an older app sends no heartbeat and no new code (it reports
 * `MCP_NOT_CONNECTED` for a PAT failure), and pages feature-detect the queue.
 * Later optional additions keep grooming at 3, and pages feature-detect them:
 * `refreshUsage` and the queue state's `usageCheck` (PKG-PM-C Mac walk,
 * 2026-10-23), and `app.reload` / `app.quit` for the Windows title-bar
 * account menu (SpecTree Windows walk 1, gap G-02, 2026-10-27).
 *
 * grooming 4 (epic PI-SPEC-SW-E323, ER-1710): agendas stay editable until
 * the meeting date passes. A prepared (`completed`) agenda goes back to
 * `running` when items are added to it before the meeting, so the queue
 * briefs only the new items; the engine's item report on an item taken off
 * the agenda mid-run is answered with `GROOMING_BATCH_ITEM_REMOVED`, which
 * the app shows as a skip (`GROOMING_QUEUE_ITEM_REMOVED_TEXT`), never a
 * failure. Adds the API error codes `GROOMING_AGENDA_LOCKED`,
 * `GROOMING_BATCH_ITEM_REMOVED`, `GROOMING_SESSION_DATE_NOT_UPCOMING` and
 * `GROOMING_SESSION_DATE_TAKEN` (`GROOMING_BATCH_NOT_DRAFT` stays for batches
 * that are not agendas), and the session schemas in `grooming/api.ts`:
 * `groomingSessionDateSchema`, `moveGroomingBatchItemBodySchema`,
 * `groomingSessionParamsSchema`, `addToSessionBodySchema`,
 * `groomingUpcomingSessionSchema`, `groomingUpcomingSessionsResponseSchema`
 * and `moveGroomingBatchItemResponseSchema`. SpecTree still serves grooming
 * 1, 2 and 3 (`min: 1`): an older app keeps its batch routes, and pages
 * feature-detect the new behaviour with `app.contracts.grooming >= 4`.
 * Leaves every other toolset unchanged.
 *
 * grooming 5 (epic PI-SPEC-SW-E324, ER-1712): a person with
 * `prepare-sessions` may disregard an unposted brief (`held`, `skipped` or
 * `failed`), so its items go back to "needs brief" and the next Prepare
 * briefs run writes a new one, and may undo that. New routes
 * `POST /api/v1/work-items/.../grooming-briefs/:briefId/disregard` and
 * `POST .../grooming-briefs/:briefId/undo-disregard`; schemas in
 * `grooming/api.ts`: `GROOMING_DISREGARD_PREVIOUS_COMMENT_STATUSES`,
 * `GROOMING_DISREGARD_PREVIOUS_ITEM_STATUSES`,
 * `GROOMING_DISREGARD_PREVIOUS_REVIEW_STATES`, `groomingDisregardUndoSchema`,
 * `disregardGroomingBriefResponseSchema`,
 * `undoDisregardGroomingBriefBodySchema` and
 * `undoDisregardGroomingBriefResponseSchema` (the undo token travels through
 * the client; SpecTree re-validates every value). Adds the API error codes
 * `GROOMING_BRIEF_POSTED` (the brief is on ADO), `GROOMING_BRIEF_DISCARDED`
 * (already disregarded) and `GROOMING_DISREGARD_UNDO_UNAVAILABLE` (a newer
 * brief exists or the item moved on), and lists the existing
 * `GROOMING_BRIEF_NOT_HELD` and `GROOMING_BRIEF_NOT_FOUND`. A locked agenda
 * refuses both routes with `GROOMING_AGENDA_LOCKED`. SpecTree still serves
 * grooming 1-4 (`min: 1`), and pages feature-detect with
 * `app.contracts.grooming >= 5`. Leaves every other toolset unchanged.
 *
 * help 1 (epic PI-SPEC-SW-E302, ER-1655): the Help chat toolset —
 * `spectreeToolbox.help` (`HelpToolsetBridge`), its events (`helpEventSchema`,
 * including streamed `answer-delta` text), the `turn.json` input, the
 * `SPECTREE_HELP_TURN` marker line, the fixed confirm-button action list
 * (`HELP_ACTIONS`), the in-app link allowlist, the screen keys, the image
 * limits and the Help inbox API schemas (`help/`). SpecTree serves help 1
 * (`min: 1`). Adding it leaves grooming at 3 and formulator at 2. Contract
 * ids are additive: removing `help` later is a breaking change.
 *
 * fogbugz 1 (epic PI-SPEC-SW-E307, ER-1663): the FogBugz toolset —
 * `spectreeToolbox.fogbugz` (`FogbugzToolsetBridge`) with one operation,
 * `summarizeEvtx`: the person's own Claude Code writes the four summary
 * sections from the deterministic EVTX digest SpecTree returns, the page
 * shows the result composed by `composeToolboxEvtxSummary`, and SpecTree
 * posts the reviewed sections to the existing FogBugz case. Also the digest
 * response, summarize input, `SPECTREE_FOGBUGZ_EVTX` marker line and
 * reviewed-post body schemas, the engine error codes and the character caps
 * (`fogbugz/`). SpecTree serves fogbugz 1 (`min: 1`). Adding it leaves
 * grooming at 3, formulator at 2 and help at 1. A new toolset id rather than
 * a standalone engine. The FogBugz Formulator does not extend it: it is its
 * own toolset, `fogbugz-formulator` 1 below (epic PI-SPEC-SW-E309, owner
 * decision 43059464), so fogbugz stays at 1.
 * Still within fogbugz 1 (unpublished, feature PI-SPEC-SW-1602): "Look into
 * the code" — `listCodeRefs`, `investigateCode`, `askCode` and
 * `onCodeProgress` (`fogbugz/code.ts`); `summarizeEvtx` and `cancel` keep
 * their shapes.
 * Still fogbugz 1 (additive, ER-1664 Package D): link and person-key read
 * schemas in fogbugz/link.ts and fogbugz/reads.ts; no bridge or engine
 * change. `link.ts` also carries the ADO area-path picker list
 * (`adoAreaPathListSchema`, `ADO_AREA_PATHS_MAX`).
 *
 * fogbugz-formulator 1 (epic PI-SPEC-SW-E309, ER-1665 Package C): the
 * Toolbox FogBugz Formulator toolset — `spectreeToolbox.fogbugzFormulator`
 * (`FogbugzFormulatorToolsetBridge`: `start`, `answer`, `addImage`,
 * `addEventLog`, `summarizeEventLog`, `listAttachments`, `readAttachment`,
 * `cancel`, `forget`, `onEvent`), its events (`fogbugzFormulatorEventSchema`),
 * the `turn.json` input, the `SPECTREE_FOGBUGZ_FORMULATOR_TURN` marker line,
 * the engine turn, session view, start body, review, Bug-bar result and
 * submit schemas, the error codes and the attachment caps
 * (`fogbugz-formulator/`). SpecTree serves fogbugz-formulator 1 (`min: 1`).
 * Adding it leaves grooming at 3, formulator at 2, help at 1 and fogbugz at
 * 1. Its own toolset id rather than fogbugz 2 (owner decision 43059464), so
 * the Formulator and the FogBugz event log summary version independently.
 *
 * ask 1 (epic PI-SPEC-SW-E310, ER-1667 Package F): the Product Q&A toolset —
 * `spectreeToolbox.ask` (`AskToolsetBridge`: `getStatus`, `checkProduct`,
 * `ask`, `cancel`, `closeConversation`, `onEvent`), its events
 * (`askEventSchema`, including streamed `answer-delta` text), the product
 * scope (an ADO project, or a FogBugz project resolved to one), the
 * `turn.json` input, the `SPECTREE_ASK_TURN` marker line with its verdicts
 * and normaliser, the image limits (aliases of Help's), the engine error
 * codes and the products API schemas (`ask/`). SpecTree serves ask 1
 * (`min: 1`). Adding it leaves grooming at 3, formulator at 2, help at 1,
 * fogbugz at 1 and fogbugz-formulator at 1. The FogBugz scope variant is part
 * of ask 1 from the start, so wiring it later keeps the number. Contract ids
 * are additive: removing `ask` later is a breaking change.
 *
 * fogbugz-to-ado 1 (epic PI-SPEC-SW-E311, ER-1666 Package E): the FogBugz
 * case → ADO Bug toolset for Project Managers and Admins —
 * `spectreeToolbox.fogbugzToAdo` (`FogbugzToAdoToolsetBridge`:
 * `checkRepos`, `readSlots`, `draftBug`, `cancel`, `onProgress`), the
 * `SPECTREE_FOGBUGZ_TO_ADO` marker line with its slots and draft shapes, the
 * case bundle, prepare refusal (with the ADO Formulator hand-off for a
 * Feature case), confirmed slots, draft, reporter questions, classification
 * and create schemas, the engine error codes, the attachment and character
 * caps and the pure composers (`fogbugz-to-ado/`). SpecTree serves
 * fogbugz-to-ado 1 (`min: 1`). Adding it leaves grooming at 3, formulator at
 * 2 and help, fogbugz, fogbugz-formulator and ask at 1. Its own toolset id
 * rather than fogbugz 2, so the PM-only tool versions independently of the
 * `fogbugz` and `fogbugz-formulator` toolsets. Contract ids are additive:
 * removing `fogbugz-to-ado` later is a breaking change.
 */
export const TOOLBOX_CONTRACTS = {
    grooming: 5,
    formulator: 4,
    help: 1,
    fogbugz: 1,
    "fogbugz-formulator": 1,
    ask: 1,
    "fogbugz-to-ado": 1,
};
/** Every toolset id, in display order. */
export const TOOLSET_IDS = [
    "grooming",
    "formulator",
    "help",
    "fogbugz",
    "fogbugz-formulator",
    "ask",
    "fogbugz-to-ado",
];
/** One toolset's range as `GET /api/v1/version` reports it. */
export const toolboxContractRangeSchema = z
    .object({ current: z.number().int().min(1), min: z.number().int().min(1) })
    .refine((range) => range.min <= range.current, { message: "min must not be above current" });
/**
 * The `toolboxContracts` field of `GET /api/v1/version`. Keyed by toolset id;
 * an unknown toolset id from a newer SpecTree is kept, not refused.
 */
export const toolboxContractsSchema = z.record(z.string(), toolboxContractRangeSchema);
/**
 * The `toolboxBundledMcp` field of `GET /api/v1/version` (epic
 * PI-SPEC-SW-E288): the oldest SpecTree MCP server version this SpecTree
 * accepts from the copy the Toolbox ships and runs itself. A SpecTree that
 * predates it omits the field: then any bundled server is accepted.
 */
export const toolboxBundledMcpSchema = z.object({ min: z.string().min(1) });
/** The part of `GET /api/v1/version` the Toolbox reads. Other fields are stripped. */
export const toolboxVersionResponseSchema = z.object({
    version: z.string(),
    toolboxContracts: toolboxContractsSchema.optional(),
    /** Optional (epic PI-SPEC-SW-E288): absent from older SpecTree releases. */
    toolboxBundledMcp: toolboxBundledMcpSchema.optional(),
});
/** The numeric parts of a dotted version; a pre-release or build suffix is ignored, a missing or bad part is 0. */
function versionParts(version) {
    const core = version.trim().replace(/^v/i, "").split(/[-+]/)[0] ?? "";
    return core.split(".").map((part) => {
        const n = Number.parseInt(part, 10);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    });
}
/** Compare two dotted versions: negative when `a` is older, 0 when equal, positive when newer. */
export function compareDottedVersions(a, b) {
    const left = versionParts(a);
    const right = versionParts(b);
    const length = Math.max(left.length, right.length);
    for (let i = 0; i < length; i += 1) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0)
            return diff;
    }
    return 0;
}
/**
 * The newer of the Windows and macOS releases (Windows on a tie, the pilot's
 * platform); the one present when only one is; undefined when neither is.
 * The SpecTree API picks the release note from it and the download page names
 * it in its lead sentence, so both agree.
 */
export function newestToolboxRelease(windows, mac) {
    if (!windows)
        return mac;
    if (!mac)
        return windows;
    return compareDottedVersions(mac.version, windows.version) > 0 ? mac : windows;
}
/**
 * True when the app's bundled MCP server (`bundled`) is older than the
 * minimum SpecTree accepts (`min`, from `toolboxBundledMcp.min`): the app
 * shows the `required` update notice. Equal or newer is not too old.
 */
export function isBundledMcpTooOld(bundled, min) {
    return compareDottedVersions(bundled, min) < 0;
}
/** Compare SpecTree's range for a toolset with the app's contract number. */
export function compareContract(server, client) {
    if (client < server.min)
        return "app-update-needed";
    if (client > server.current)
        return "spectree-update-needed";
    return "supported";
}
// ---------------------------------------------------------------------------
// Update feed
// ---------------------------------------------------------------------------
/** Path of the SpecTree-served `electron-updater` generic feed (append `/<file>`). */
export const TOOLBOX_UPDATE_FEED_PATH = "/api/v1/toolbox/updates";
/** Error code when SpecTree has no update container configured (HTTP 503). */
export const TOOLBOX_UPDATES_NOT_CONFIGURED = "TOOLBOX_UPDATES_NOT_CONFIGURED";
/** Longest feed file name accepted. */
export const TOOLBOX_UPDATE_FILE_NAME_MAX = 200;
const UPDATE_MANIFESTS = new Set(["latest.yml", "latest-mac.yml"]);
const UPDATE_FILE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ._-]*\.(zip|dmg|exe|blockmap)$/;
/**
 * True when `name` is a file the update feed may serve: `latest.yml`,
 * `latest-mac.yml`, or a `.zip`, `.dmg`, `.exe` or `.blockmap` file name with
 * no path separators, no `..` and no leading dot or dash.
 */
export function isToolboxUpdateFileName(name) {
    if (name.length === 0 || name.length > TOOLBOX_UPDATE_FILE_NAME_MAX)
        return false;
    if (name.includes("/") || name.includes("\\") || name.includes(".."))
        return false;
    return UPDATE_MANIFESTS.has(name) || UPDATE_FILE_PATTERN.test(name);
}
// ---------------------------------------------------------------------------
// Update notice and latest release (epic PI-SPEC-SW-E288)
// ---------------------------------------------------------------------------
export * from "./updates.js";
//# sourceMappingURL=contracts.js.map